/**
 * Cluster Component
 *
 * A layout primitive for horizontal arrangements that wrap.
 * Perfect for tags, buttons, badges, and other inline elements.
 *
 * Features:
 * - Horizontal flex with wrap by default
 * - Gap-based spacing (no margins needed)
 * - Alignment and justification options
 * - Uses Tailwind spacing scale (no arbitrary values)
 *
 * @example Basic usage
 * ```tsx
 * <Cluster gap="sm">
 *   <Badge>Tag 1</Badge>
 *   <Badge>Tag 2</Badge>
 *   <Badge>Tag 3</Badge>
 * </Cluster>
 * ```
 *
 * @example Button group
 * ```tsx
 * <Cluster gap="md" justify="end">
 *   <Button variant="outline">Cancel</Button>
 *   <Button>Save</Button>
 * </Cluster>
 * ```
 *
 * @example Centered tags
 * ```tsx
 * <Cluster gap="xs" justify="center">
 *   {tags.map(tag => <Badge key={tag}>{tag}</Badge>)}
 * </Cluster>
 * ```
 */

import * as React from 'react';
import type {VariantProps} from 'class-variance-authority';
import {cn} from '@/shared/utils/utils';
import {clusterVariants} from './Cluster.variants';

// =============================================================================
// TYPES
// =============================================================================

interface ClusterProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof clusterVariants> {
  /**
   * The HTML element to render as
   * @default 'div'
   */
  as?: "div" | "span" | "nav";
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Cluster - Horizontal wrapping layout primitive
 *
 * Use Cluster for horizontal arrangements of elements that should
 * wrap to the next line when they run out of space.
 *
 * Pages and features MUST use this instead of ad-hoc flex containers.
 *
 * Replaces patterns like:
 * - `className="flex flex-wrap gap-2"`
 * - `className="flex items-center gap-4"`
 * - `className="flex flex-wrap justify-center gap-3"`
 */
const Cluster = React.memo(function Cluster({
  as: Component = "div",
  gap,
  align,
  justify,
  wrap,
  fullWidth,
  className,
  children,
  ...props
}: ClusterProps): React.ReactElement {
  return (
    <Component
      className={cn(
        clusterVariants({ gap, align, justify, wrap, fullWidth }),
        className
      )}
      data-layout="cluster"
      {...props}
    >
      {children}
    </Component>
  );
});

Cluster.displayName = "Cluster";

// =============================================================================
// EXPORTS
// =============================================================================


export { Cluster,  };
